import React, { FC, useCallback, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { FlatList, LayoutChangeEvent, Platform, useWindowDimensions } from 'react-native';

import Alert from 'components/alert';
import { AppDownloadSection } from 'components/features/components/app-download-section';
import { MarketingTileGroup } from 'components/features/components/marketing-tile-group';
import { RewardsSection } from 'components/features/components/rewards-section';
import { SimpleHomePage } from 'components/features/components/simple-home-page';
import { Footer } from 'components/footer';
import { LayoutContainer } from 'components/layout/styled';
import LoadingAnimation from 'components/loading-animation';
import { QuestSection } from 'components/quests';
import { IMarketingCardGroupFragment, IMarketingTileGroup } from 'generated/sanity-graphql';
import { useIsDesktopLargeBreakpoint, useIsMobileBreakpoint } from 'hooks/use-media-query';
import { isNative } from 'utils/environment';

import { Section, Spacer } from './home-page/styles';
import { Component, Components } from './home-page/types';

const itemIdExtractor = (item: Component | null, index: number) => item?._id || index + '_item';

const Spinner = () => (
  <LayoutContainer>
    <Box padding="$2">
      <LoadingAnimation />
    </Box>
  </LayoutContainer>
);

let previousHeight = 0;
const APPROXIMATE_CHROME_HEIGHT_IOS = 213;
const APPROXIMATE_CHROME_HEIGHT_ANDROID = 144;

export const Features: FC<React.PropsWithChildren<{
  components: Components;
  headerComponent?: React.ReactElement | null;
}>> = React.memo(({ headerComponent, components }) => {
  const componentLength = components?.length || 0;
  const isMobile = useIsMobileBreakpoint();
  const isDesktopLarge = useIsDesktopLargeBreakpoint();
  const { height, width } = useWindowDimensions();
  const [flatListDimensions, setFlatListDimensions] = useState<{
    height: number;
    width: number;
  } | null>({
    // prevent large amounts of jank as the screen is resized based on various nav bar conditions
    height:
      previousHeight ??
      height -
        (Platform.OS === 'web'
          ? 0
          : Platform.OS === 'ios'
          ? APPROXIMATE_CHROME_HEIGHT_IOS
          : APPROXIMATE_CHROME_HEIGHT_ANDROID),
    width,
  });

  previousHeight = flatListDimensions?.height || 0;

  const renderItem = useCallback(
    ({ item, index }: { item: Component | null; index: number }) => {
      if (!item) {
        return null;
      }

      const first = index === 0;
      const last = index + 1 === componentLength;
      switch (item.__typename) {
        case 'Spacer': {
          return <Spacer $isSmall={item.isSmall} />;
        }

        case 'SimpleHomePage': {
          return <SimpleHomePage item={item} flatListDimensions={flatListDimensions} />;
        }

        case 'Alert': {
          return (
            <Alert
              headline={item.heading?.locale || ''}
              body={item.body?.locale || ''}
              learnMore={item.learnMoreText?.locale || ''}
              learnMoreUrl={item.learnMoreUrl?.locale || ''}
            />
          );
        }

        case 'MarketingCardGroup':
          return (
            <LayoutContainer mt={'$4'}>
              <Section first={first} last={last} shouldRenderDefaultBackground={true}>
                <MarketingTileGroup item={(item as unknown) as IMarketingCardGroupFragment} />
              </Section>
            </LayoutContainer>
          );

        case 'MarketingTileGroup':
          return (
            <LayoutContainer>
              <Section first={first} last={last}>
                <MarketingTileGroup item={item as IMarketingTileGroup} />
              </Section>
            </LayoutContainer>
          );

        case 'RewardsSection': {
          return (
            <LayoutContainer isFullContainer={isMobile}>
              <RewardsSection item={item} />
            </LayoutContainer>
          );
        }

        case 'QuestsSection': {
          return (
            <LayoutContainer>
              <QuestSection />
            </LayoutContainer>
          );
        }

        case 'AppDownload': {
          return <AppDownloadSection item={item.details} />;
        }

        case 'Footer': {
          return <Footer />;
        }

        default: {
          return null;
        }
      }
    },
    [componentLength, isMobile, flatListDimensions]
  );

  const [isLoadingMore, setIsLoadingMore] = useState(true);
  const setIsLoadingMoreFalse = useCallback(() => setIsLoadingMore(false), []);

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => setFlatListDimensions(e.nativeEvent.layout),
    []
  );

  if (!components) {
    return null;
  }

  return (
    <FlatList
      onLayout={onLayout}
      ListHeaderComponent={headerComponent}
      ListFooterComponent={isLoadingMore ? Spinner : null}
      keyExtractor={itemIdExtractor}
      data={components}
      initialNumToRender={2}
      renderItem={renderItem}
      onEndReached={setIsLoadingMoreFalse}
      // Changed from 0 to 0.01 because with 0 onEndReached was not being called on web
      // 0.01 is a small value, close to the original, that works on both web and mobile
      onEndReachedThreshold={0.01}
      {...(isNative && {
        windowSize: 1,
      })}
      showsVerticalScrollIndicator={isDesktopLarge}
    />
  );
});
